/**
 * What: The "*" that marks a required field or section, with the Worker's reason as hover text. One helper for every kind.
 * Transport / data-flow: Pure presentation. The Worker puts `requiredReason` on each field the person must fill in; this file
 *   draws it and decides nothing. A text, number or select field is also marked `required` with its own label kept for
 *   assistive tech only, so a screen reader says "required" and the visible label and "*" are not read twice. An image
 *   option has no such control, so `UploadBlock` reads out the Worker's reason text instead.
 */
import type { BuilderBlock } from '@testfeed/shopify-contracts'
import type { ReactNode } from 'react'

/**
 * The reason a thing is required; undefined when it is optional. ONE reading for a field block and for a section header:
 * both carry the same `requiredReason`, and both are drawn through `RequiredMark`.
 */
export function requiredReasonOf(thing: BuilderBlock | { requiredReason?: string }): string | undefined {
  return 'requiredReason' in thing ? thing.requiredReason : undefined
}

const markId = (key: string) => `required-${key.replaceAll('.', '-')}`

/** The "*" alone, for a spot that already draws the label (an option row's badge). */
export function RequiredMark({ markKey, reason }: { markKey: string; reason: string }) {
  return (
    <>
      <s-text tone="critical" interestFor={markId(markKey)} accessibilityVisibility="hidden">
        *
      </s-text>
      <s-tooltip id={markId(markKey)}>{reason}</s-tooltip>
    </>
  )
}

/** A field whose label is drawn here with the "*" beside it; the field below carries the label for assistive tech only. */
export function RequiredField({
  markKey,
  label,
  reason,
  children,
}: {
  markKey: string
  label: string
  reason: string
  children: ReactNode
}) {
  return (
    <s-stack direction="block" gap="small-300">
      <s-stack direction="inline" gap="small-100" alignItems="center">
        <s-text type="strong" accessibilityVisibility="hidden">
          {label}
        </s-text>
        <RequiredMark markKey={markKey} reason={reason} />
      </s-stack>
      {children}
    </s-stack>
  )
}
